import { FirebaseError } from 'firebase/app';
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
} from 'firebase/firestore';

import { db } from '../../config';

export const fetchPaginatedCollectionRealtime = async <T>(
  collectionPath: string,
  page: number,
  pageSize: number,
  orderField: keyof T,
  onData: (data: T[], totalPages: number) => void,
  onError: (error: FirebaseError) => void,
): Promise<() => void> => {
  const collectionRef = collection(db, collectionPath);

  let totalDocuments: number;
  try {
    const countSnapshot = await getCountFromServer(collectionRef);
    totalDocuments = countSnapshot.data().count;
  } catch (e) {
    const error = e instanceof FirebaseError
      ? e
      : new FirebaseError('unknown', 'Failed to get document count');
    onError(error);
    return () => {
      // eslint-disable-next-line no-console
      console.error(error);
    };
  }

  if (totalDocuments === 0) {
    onData([], 0);
    return () => {
      // eslint-disable-next-line no-console
      console.error(totalDocuments);
    };
  }

  const totalPages = Math.ceil(totalDocuments / pageSize);

  if (page > totalPages || page < 1) {
    const error = new FirebaseError('invalid-argument', `Invalid page number: ${page}`);
    onError(error);
    throw error;
  }

  let startAfterDoc: QueryDocumentSnapshot | null = null;

  if (page > 1) {
    const offset = (page - 1) * pageSize;
    const offsetSnapshot = await getDocs(
      query(
        collectionRef,
        orderBy(orderField as string, 'desc'),
        limit(offset),
      ),
    );
    startAfterDoc = offsetSnapshot.docs[offsetSnapshot.docs.length - 1] ?? null;
  }

  const paginatedQuery = startAfterDoc
    ? query(
      collectionRef,
      orderBy(orderField as string, 'desc'),
      startAfter(startAfterDoc),
      limit(pageSize),
    )
    : query(
      collectionRef,
      orderBy(orderField as string, 'desc'),
      limit(pageSize),
    );

  const unsubscribe = onSnapshot(
    paginatedQuery,
    (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as unknown as T[];

      onData(data, totalPages);
    },
    (error) => {
      onError(error as FirebaseError);
    },
  );

  return unsubscribe;
};
